import React, { useRef, Suspense, lazy, useState, useEffect } from "react";
import Hero from "./Hero";
import AboutPreview from "./AboutPreview";
import WhySortedTrips from "./WhySortedTrips";
import StickyUpcomingTripsBtn from "./StickyUpcomingTripsBtn";
import AboutUsAPI from "../api/AboutUsAPI";
import CalendarAPI from "../api/CalendarAPI";

// Lazy load heavier components for better performance
const UpcomingTrips = lazy(() => import("./UpcomingTrips"));
const ReviewPreview = lazy(() => import("./ReviewPreview"));
const FAQ = lazy(() => import("./FAQ"));
const SpecialTrips = lazy(() => import("./SpecialTrips"));
const SpecialTripsDropDown = lazy(() => import("./SpecialTripsDropDown"));
const Destination = lazy(() => import("./Destination"));

const Home = () => {
  const upcomingTripsRef = useRef(null);
  const faqRef = useRef(null);

  let [aboutShortDescription, setAboutShortDescription] = useState([]);
  let [aboutHomePageDescription, setAboutHomePageDescription] = useState([]);

  let [upcomingTripList, setUpcomingTripList] = useState([]);

  const [error, setError] = useState(null);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    const fetchSequentialData = async () => {
      try {
        // Step 1: Fetch about data
        await fetchData();

        // Step 2: Wait 2 seconds
        await delay(1000);

        // Step 3: Fetch trips data
        await fetchUpcomingTrips();
      } catch (err) {
        console.error("Error during sequential fetch:", err);
      }
    };

    fetchSequentialData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await AboutUsAPI.getAboutUs();

      if (res.code === 200) {
        const result = res.data;

        setAboutShortDescription(result.short_description);
        setAboutHomePageDescription(result.homepage_description);
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Unable to load data.");
    }
  };

  const fetchUpcomingTrips = async (year, month) => {
    try {
      // Agar year ya month undefined/blank ho to current year/month set karo
      const currentDate = new Date();
      const queryYear = year || currentDate.getFullYear();
      const queryMonth = month || currentDate.getMonth() + 1; // getMonth() returns 0-11

      // API params
      const params = {
        year: queryYear,
        month: queryMonth,
      };

      const res = await CalendarAPI.getTripCardInfo(params);

      if (res.code === 200) {
        setUpcomingTripList(res.data);
      }
    } catch (monthError) {
      console.error(
        `Error fetching trips for ${month || "current"}/${year || "current"}:`,
        monthError
      );
    }
  };

  return (
    <>
      <Hero />

      {/* Wrap lazy components in Suspense for fallback UI */}
      <Suspense fallback={<div className="text-center py-5">Loading...</div>}>
        <div ref={upcomingTripsRef}>
          <UpcomingTrips />
        </div>
      </Suspense>

      <Suspense fallback={<div className="text-center py-5">Loading...</div>}>
        <SpecialTrips upcomingTripList={upcomingTripList} />
      </Suspense>

      <Suspense fallback={<div className="text-center py-5">Loading...</div>}>
        <SpecialTripsDropDown />
      </Suspense>

      <Suspense fallback={<div className="text-center py-5">Loading...</div>}>
        <Destination />
      </Suspense>

      <AboutPreview data={aboutShortDescription} />

      <WhySortedTrips data={aboutHomePageDescription} />

      <Suspense fallback={<div className="text-center py-5">Loading...</div>}>
        <ReviewPreview />
      </Suspense>

      <div ref={faqRef}>
        <Suspense fallback={<div className="text-center py-5">Loading...</div>}>
          <FAQ />
        </Suspense>
      </div>

      {/* Sticky button always available, safe fallback for refs */}
      <StickyUpcomingTripsBtn
        upcomingTripsRef={upcomingTripsRef || { current: null }}
        faqRef={faqRef || { current: null }}
      />
    </>
  );
};

export default React.memo(Home); // memoize to prevent re-renders
